import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { FormAutosuggest, useWatch } from '@fs/zion-form'
import { ContentDelete } from '@fs/zion-icon'
import { Cell, Grid, IconButton, LabelSpacer, useOverlay } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { parseRelKey } from '../../shared/relationships/relationshipQueries'
import RemoveHouseholdMemberDialogOverlay from './RemoveHouseholdMemberDialogOverlay'

/**
 * RelationshipInputs
 *
 * Renders a pair of autosuggest inputs for selecting a person's name and their relationship
 * to the primary person in a household. Includes add/remove actions and handles both new
 * and existing relationships.
 *
 * Features:
 * - Name selection with autosuggest and "Add Person" last resort option
 * - Relationship type selection with gender-appropriate suggestions
 * - Delete button for removing household members
 * - Conditional field disabling for existing/linked relationships
 * - Overlays for adding new persons and confirming removal
 *
 * @component
 * @param {Object} props
 * @param {Object} props.refs - React ref object containing an array of refs for name input fields
 * @param {Object} props.field - Field object from react-hook-form's useFieldArray
 * @param {string} props.field.id - Unique field identifier
 * @param {boolean} [props.field.isDeleted] - Whether the field is marked as deleted
 * @param {number} props.index - Array index of this relationship in the form, from the useFieldArray map function
 * @param {Array} props.namesAutoSuggestions - Array of name suggestion objects for the autosuggest
 * @param {Object} props.formMethods - React Hook Form methods object from useZionForm
 * @param {Object} props.fieldArrayMethods - Field array methods from useFieldArray (update, remove, etc.)
 * @param {boolean} [props.dialogMode=false] - Whether component is rendered in dialog (overlay) mode (affects layout/behavior)
 * @param {Function} [props.onRelationshipChange] - Callback fired when relationship type changes
 * @param {Function} [props.onNameChange] - Callback fired when name selection changes
 * @param {Array} [props.relationshipSuggestions] - Array of relationship suggestion objects for the relationship autosuggest
 *
 * @returns {JSX.Element|null} The relationship input fields or null if the field is deleted
 */
const RelationshipInputs = ({
  refs,
  field,
  index,
  namesAutoSuggestions,
  formMethods,
  fieldArrayMethods,
  dialogMode = false,
  onRelationshipChange = () => {},
  onNameChange = () => {},
  relationshipSuggestions = [],
}) => {
  const [t] = useTranslation()
  const removeOverlay = useOverlay()
  const { update, remove } = fieldArrayMethods

  const [relationship, targetRecordId] = useMemo(() => {
    return formMethods.getValues([`relationships[${index}]`, 'targetRecordId'])
  }, [formMethods, index])

  /**
   * Handles changes to the relationship type selection.
   * Updates form fields with the new relationship type and direction,
   * then notifies parent component of the change.
   *
   * @param {Object} suggestion - The selected relationship suggestion object
   * @param {string} suggestion.key - The relationship key containing type and direction
   */
  const handleRelationshipChange = useCallback(
    (suggestion) => {
      const { relKey, order } = parseRelKey(suggestion?.key)

      // Update derived form fields only
      formMethods.setValue(`relationships[${index}].relationshipType`, relKey, { shouldDirty: true })
      formMethods.setValue(`relationships[${index}].relationshipDirection`, order, { shouldDirty: true })
      // FormAutosuggest already sets `relationships[${index}].relationship` via the name prop

      // Inform parent about the change
      onRelationshipChange(suggestion, field, index)
    },
    [formMethods, index, field, onRelationshipChange]
  )

  /**
   * Handles changes to the name selection.
   * Updates form with the selected person's ID and record ID.
   * Triggers form validation and notifies parent component.
   * If no suggestion provided (cleared), resets the personId field.
   *
   * @param {Object|null} suggestion - The selected name suggestion object or null if cleared
   * @param {string} [suggestion.key] - The person ID
   * @param {string} [suggestion.recordId] - The record ID associated with the person
   */
  const handleNameChange = useCallback(
    (suggestion) => {
      // Handle clear/empty selection
      if (!suggestion?.key) {
        formMethods.setValue(`relationships[${index}].personId`, null, { shouldDirty: true })
        // FormAutosuggest already clears `relationships[${index}].value` via the name prop

        // Inform parent about the name being cleared
        onNameChange(null, field, index)
        return
      }

      // Update derived personId field only
      formMethods.setValue(`relationships[${index}].personId`, suggestion.key, { shouldDirty: true })
      // FormAutosuggest already sets `relationships[${index}].value` via the name prop
      formMethods.setValue(`relationships[${index}].recordId`, suggestion.recordId || null, { shouldDirty: true })

      // Trigger validation for the whole relationship group
      formMethods.trigger('relationships')

      // Inform parent about the name change
      onNameChange(suggestion, field, index)
    },
    [formMethods, index, field, onNameChange]
  )

  /**
   * Handles deletion of a relationship row.
   * For existing relationships (linked to SLS or matching target record),
   * opens confirmation dialog. For new relationships, removes immediately.
   * Triggers form validation after deletion.
   */
  const handleDelete = useCallback(() => {
    if ((relationship?.slsRelationshipId || relationship?.recordId === targetRecordId) && !relationship?.isNew) {
      // Existing relationship - open remove dialog to confirm
      removeOverlay.open()
    } else {
      // New relationship - remove from array entirely
      remove(index)
    }
    formMethods.trigger('relationships')
  }, [relationship, removeOverlay, remove, index, targetRecordId, formMethods])

  /**
   * Handles save action from the remove confirmation dialog.
   * Marks the existing relationship as deleted instead of removing
   * it from the array, preserving the index structure.
   */
  const handleSaveOnRemoveDialog = useCallback(() => {
    // Existing relationship - mark as deleted
    update(index, { ...relationship, isDeleted: true })
  }, [index, relationship, update])

  const selectedPersonId = useWatch({ name: `relationships[${index}].personId`, control: formMethods.control })

  // If the field is marked as deleted, do not render it, we handle this becaause we need to keep the indexes consistent
  if (field.isDeleted) {
    return null
  }

  return (
    <Grid key={field.id} gutters="nano" alignY="middle">
      <FormAutosuggest
        ref={
          dialogMode
            ? undefined
            : (el) => {
                refs.current[index] = el
              }
        }
        columns={dialogMode ? '8' : '6'}
        data-testid={`familyMemberNameField-${index}`}
        label="Name"
        suggestions={namesAutoSuggestions}
        name={`relationships[${index}].value`}
        onSuggestionSelected={handleNameChange}
        disabled
        clearable
        dense
      />

      <FormAutosuggest
        columns={dialogMode ? '4' : '5'}
        data-testid={`familyMemberRelationshipField-${index}`}
        suggestions={relationshipSuggestions}
        label="Relationship"
        name={`relationships[${index}].relationship`}
        onSuggestionSelected={handleRelationshipChange}
        placeholder={t('household-details-card.relationship-placeholder', 'Select...')}
        clearable
        dense
      />

      {!dialogMode && (
        <Cell columns="1">
          <LabelSpacer />
          <IconButton
            Icon={ContentDelete}
            color="red"
            onClick={handleDelete}
            data-testid={`household-member-delete-button-${index}`}
            title={t('household-details-card.remove-from-household-button', 'Remove from household')}
          />

          <RemoveHouseholdMemberDialogOverlay
            overlay={removeOverlay}
            personId={selectedPersonId}
            onSave={handleSaveOnRemoveDialog}
          />
        </Cell>
      )}
    </Grid>
  )
}

RelationshipInputs.propTypes = {
  refs: PropTypes.shape({
    // eslint-disable-next-line react/forbid-prop-types
    current: PropTypes.array.isRequired,
  }).isRequired,
  field: PropTypes.shape({
    id: PropTypes.string.isRequired,
    isDeleted: PropTypes.bool,
  }).isRequired,
  index: PropTypes.number.isRequired,
  namesAutoSuggestions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      recordId: PropTypes.string,
      primaryText: PropTypes.string,
    })
  ).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  formMethods: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  fieldArrayMethods: PropTypes.object.isRequired,
  dialogMode: PropTypes.bool,
  onRelationshipChange: PropTypes.func,
  onNameChange: PropTypes.func,
}

export default RelationshipInputs
